import { cn } from "@suite/lib/utils";

interface Props {
  /** Hex hash the glyph is derived from. Same hash → same glyph, always. */
  hash: string;
  className?: string;
}

const GRID = 7;
const HALF = Math.ceil(GRID / 2);
const CELL = 10;
const GAP = 3;
const SIZE = GRID * CELL + (GRID - 1) * GAP;

/** Map a hex nibble (0–15) to a cell opacity — thresholds tuned so roughly half the
 * cells read as "on". The pattern is a quick visual check (same hash → same glyph), not a
 * collision-free encoding: it reads 28 of the 64 nibbles and quantises them to 4 levels,
 * so different hashes can occasionally draw the same picture. */
function cellOpacity(nibble: number): number {
  if (nibble >= 12) return 0.95;
  if (nibble >= 8) return 0.55;
  if (nibble >= 5) return 0.22;
  return 0.07;
}

/**
 * Deterministic identicon for a fingerprint hash: a 7×7 grid, mirrored around the
 * vertical axis, with cell intensity taken from the hash's hex digits. Purely visual —
 * a way to *see* the hash change (or not) at a glance across recomputes and devices.
 */
export default function FingerprintGlyph({ hash, className }: Props) {
  const cells: { x: number; y: number; opacity: number; key: string }[] = [];
  for (let row = 0; row < GRID; row++) {
    for (let col = 0; col < GRID; col++) {
      const src = col < HALF ? col : GRID - 1 - col;
      const nibble = parseInt(hash[(row * HALF + src) % hash.length] ?? "0", 16);
      cells.push({
        x: col * (CELL + GAP),
        y: row * (CELL + GAP),
        opacity: cellOpacity(Number.isNaN(nibble) ? 0 : nibble),
        key: `${row}-${col}`,
      });
    }
  }

  return (
    // Decorative to assistive tech: every call site provides its own text alternative (or is
    // itself aria-hidden), and the SVG's own label used to double up with that description.
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className={cn("text-brand-green", className)} aria-hidden>
      {cells.map((c) => (
        <rect
          key={c.key}
          x={c.x}
          y={c.y}
          width={CELL}
          height={CELL}
          rx={2.5}
          fill="currentColor"
          opacity={c.opacity}
        />
      ))}
    </svg>
  );
}
